import { useState, useEffect } from 'react';

/**
 * メディアクエリの状態を監視するカスタムフック
 * @param query - メディアクエリ文字列（例: '(max-width: 768px)'）
 * @returns メディアクエリがマッチしているかどうか
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);

    // 初期値を設定
    setMatches(mediaQuery.matches);

    // リスナーを設定
    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // モダンブラウザ用
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      // レガシーブラウザ用（IE11など）
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
  }, [query]);

  return matches;
};

/**
 * モバイルデバイスかどうかを判定するカスタムフック
 * @param breakpoint - ブレークポイント（デフォルト: 768px）
 * @returns モバイルデバイスかどうか
 */
export const useIsMobile = (breakpoint: number = 768): boolean => {
  return useMediaQuery(`(max-width: ${breakpoint - 1}px)`);
};

/**
 * タブレットデバイスかどうかを判定するカスタムフック
 * @returns タブレットデバイスかどうか
 */
export const useIsTablet = (): boolean => {
  return useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
};

/**
 * デスクトップデバイスかどうかを判定するカスタムフック
 * @returns デスクトップデバイスかどうか
 */
export const useIsDesktop = (): boolean => {
  return useMediaQuery('(min-width: 1024px)');
};
